import { useIsTeamJoinBootLoaderEnabled } from '@/app-shell/flags/workspace-flags';
import { JoinTeamErrorTypeAtom } from '@/app-shell/modals/team-member-join-fail-modal';
import { getCurrentUser } from '@/hooks/query/useQueryUser';
import { trackSignUpViaTeamInvite } from '@/lib/analytics/scribe/auth';
import {
  trackAcceptTeamInvitation,
  trackUserJoinTeamSuccess,
} from '@/lib/analytics/scribe/team';
import { logger } from '@/utils/logger/logger';
import { clearActiveTeam } from '@heidi/analytics';
import type { components } from '@heidi/schema/ml-scribe';
import { GET_TEAM_DETAILS, GET_USER } from '@heidi/workspace/contracts';
import {
  acceptTeamInvitation,
  GET_JOINED_TEAMS,
  getWorkspaceId,
  invalidateWorkspaceInvitations,
  invalidateWorkspaces,
  listWorkspaceInvitations,
  switchWorkspace,
  userSignedUpViaInvite,
  type WorkspaceSwitchPayload,
  writeTeamJoinBootNote,
} from '@heidi/workspace/data';
import { useIsWorkspaceSwitcherEnabled } from '@heidi/workspace/flags';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import { invalidateCurrentTeamBillingInfo } from '../query/useQueryTeamBilling';
import { invalidateCurrentUserBillingInfo } from '../query/useQueryUserBillingInfo';

type ErrorResponse = components['schemas']['ErrorResponse'];

export const ACCEPT_INVITATION = 'acceptInvitation';

/**
 * Resolves the workspace of the invitation's team. Must run before the accept
 * (the invitation leaves the pending list afterwards); failures are non-fatal.
 */
async function resolveInvitationWorkspace(
  teamId: string
): Promise<WorkspaceSwitchPayload | null> {
  try {
    const { invitations } = await listWorkspaceInvitations();
    const invitation = invitations.find((entry) => entry.team_id === teamId);
    return invitation?.workspace_id
      ? {
          workspaceId: invitation.workspace_id,
          workspaceName: invitation.workspace_name,
          iconUrl: invitation.icon_url,
        }
      : null;
  } catch (error) {
    logger.warn('Failed to resolve invitation workspace; skipping switch', {
      teamId,
      error,
    });
    return null;
  }
}

/**
 * Accepts a team invitation. If the joined team lives in a different workspace
 * (and the workspace switcher is enabled), also switches to that workspace and
 * resolves with it so callers can hard-navigate; resolves `null` otherwise.
 */
export function useMutateAcceptTeamInvitation() {
  const queryClient = useQueryClient();
  const isSignedUpViaInvite = useAtomValue(userSignedUpViaInvite);
  const isWorkspaceSwitcherEnabled = useIsWorkspaceSwitcherEnabled();
  const isTeamJoinBootLoaderEnabled = useIsTeamJoinBootLoaderEnabled();
  const [, setJoinTeamErrorType] = useAtom(JoinTeamErrorTypeAtom);
  return useMutation({
    mutationKey: [ACCEPT_INVITATION],
    retry: 0,
    mutationFn: async (input: {
      teamId: string;
      invitedEmail: string;
      joinMethod?: string;
    }): Promise<WorkspaceSwitchPayload | null> => {
      setJoinTeamErrorType(null);
      const target = isWorkspaceSwitcherEnabled
        ? await resolveInvitationWorkspace(input.teamId)
        : null;
      await acceptTeamInvitation(input.teamId);
      return target !== null && target.workspaceId !== getWorkspaceId() ? target : null;
    },
    onSuccess: async (target, input) => {
      if (isTeamJoinBootLoaderEnabled) writeTeamJoinBootNote();
      // Callers hard-navigate on a switch, as the manual switcher does.
      if (target !== null) {
        switchWorkspace(target);
        // The active workspace changed, but the tracking below fires before that
        // hard-navigate, so the team still held in the analytics registry belongs
        // to the workspace we left. Drop it so no event carries a team_id scoped
        // to the previous workspace; setActiveTeam re-populates it after reload.
        clearActiveTeam();
      }
      void invalidateCurrentUserBillingInfo();
      void invalidateCurrentTeamBillingInfo(input.teamId);

      void queryClient.invalidateQueries({
        queryKey: [GET_JOINED_TEAMS],
      });
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: input.teamId }],
      });
      void invalidateWorkspaces();
      void invalidateWorkspaceInvitations();
      // tracking
      trackAcceptTeamInvitation({
        teamId: input.teamId,
        invitedEmail: input.invitedEmail,
      });
      trackUserJoinTeamSuccess({
        teamId: input.teamId,
        invitedEmail: input.invitedEmail,
        joinMethod: input.joinMethod,
      });
      const user = getCurrentUser();
      if (isSignedUpViaInvite && user?.kinde_user_id)
        trackSignUpViaTeamInvite({
          authID: user.kinde_user_id || '',
        });

      // A GET started before acceptance can still carry unmanaged status. The
      // user query does not abort its transport; cancellation discards that
      // response instead of letting it overwrite the post-accept cache.
      await queryClient.cancelQueries({ queryKey: [GET_USER], exact: true });
      // Await fresh user data before SPA callers proceed. A workspace switch
      // hard-navigates, so mark stale without fetching in the departing document.
      // Keep TanStack's non-throwing refetch semantics: a failed user refresh
      // must not turn an accepted invitation into a retryable acceptance error.
      await queryClient.invalidateQueries({
        queryKey: [GET_USER],
        exact: true,
        refetchType: target === null ? 'active' : 'none',
      });
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse;
        if (errorData.detail.type) {
          setJoinTeamErrorType(errorData.detail.type);
        }
      }
    },
  });
}
